import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { GlobalErrorService } from '@services/global-error.service';
import { OpenPositionsService } from '@pages/vendor/open-positions/open-positions.service';
import { HiringManagerOpenpositionsService } from '@pages/hiring-manager/hiring-manager-openpositions/hiring-manager-openpositions.service';
import { dropdownValues} from '@interfaces/common.model';
import { PositionData } from '@interfaces/open-positions.model';
import { PersonaType, JobStatusEnum } from '@constants/global.enum';
import { OpsOpenPositionsService } from '@pages/ops-team/ops-open-positions/ops-open-positions.service';
import { filterData, formatSingleDate } from '@utils/utils';
import { yearDateFormat } from '@constants/dateFormat';
import { interval, takeWhile } from 'rxjs';
import { mapHiringRequest } from '@pages/hiring-manager/hiring-manager-openpositions/hiring-manager-openpositions.helper';
import {
  createLocationOptions,
  createOptions,
  createVendorOptions
} from './open-position-filter.helper';
import { restrictInvalidKeyPress } from '@utils/validators';
import {
  FilterPersistenceService,
  SavedFilterState,
} from '@services/filter-persistence.service';
import { OPEN_POSITION_FILTER_CONSTANTS } from './open-position-filter.constants';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-open-position-filter',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
  ],
  templateUrl: './open-position-filter.component.html',
  styleUrl: './open-position-filter.component.scss',
})
export class OpenPositionFilterComponent implements OnDestroy, OnChanges {
  @ViewChild('jobTitleDropdown') jobTitleDropdown: any;
  @ViewChild('lobDropdown') lobDropdown: any;
  @ViewChild('leaderDropdown') leaderDropdown: any;
  @ViewChild('statusDropdown') statusDropdown: any;
  @ViewChildren('emeraldDropdown') allDropdowns!: QueryList<any>;

  @Input()
  fieldsVisibility: string = '';
  @Input() openpositionData: PositionData[] = [];
  @Output() myOutput: EventEmitter<PositionData[]> = new EventEmitter();

  // Vendor persona (server-side) only: dropdown values come from the server
  // facets (distinct across the vendor's whole history, not one page), and
  // Apply emits the selected criteria for a server refetch instead of doing
  // an in-memory filter. Ops / Hiring keep the existing myOutput behaviour.
  @Input() serverFacets: { [key: string]: string[] } = {};
  @Output() filterCriteria: EventEmitter<{
    status?: string; lob?: string; leader?: string; location?: string;
  }> = new EventEmitter();

  // Constants for template usage
  readonly constants = OPEN_POSITION_FILTER_CONSTANTS;
  
  opened: boolean = false;
  filterForm: FormGroup = new FormGroup({});
  personaType = PersonaType;
  displayData: PositionData[] = [];
  rowData: PositionData[] = [];
  originalData: PositionData[] = []; // Store original unfiltered data for dropdown options
  openForm: boolean = true;
  showForm: boolean = true; 
  formKey: number = 0; 
  formInstances: number[] = [0];

  isDataLoaded: boolean = false;
  hasSavedFilters: boolean = false;

  jobTitle: dropdownValues[] = [];
  lob: dropdownValues[] = [];
  leader: dropdownValues[] = [];
  raisedOn: dropdownValues[] = [];
  requestStatus: dropdownValues[] = [];
  location: dropdownValues[] = [];
  sharedByVendor: dropdownValues[] = [];

  constructor(
    public fb: FormBuilder,
    public globalError: GlobalErrorService,
    private vendorOpenpositionsService: OpenPositionsService,
    private openPositionsService: HiringManagerOpenpositionsService,
    private opsOpenPositionsService: OpsOpenPositionsService,
    private filterPersistenceService: FilterPersistenceService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadFormData();
    this.checkForSavedFilters();
    this.cleanupOldFormatFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serverFacets'] && this.isVendorServerMode()) {
      // Defer to a clean CD cycle. `serverFacets` changes as part of the
      // parent's data-load change-detection pass; rebuilding the dropdown
      // option arrays synchronously here mutates emerald-dropdown inputs
      // mid-pass, which throws NG0100 (ExpressionChangedAfterItHasBeenChecked)
      // and aborts the pass before sibling views (e.g. the tab badge) paint.
      setTimeout(() => this.applyServerFacets());
    }
  }

  private isVendorServerMode(): boolean {
    return this.fieldsVisibility === PersonaType.vendorRepOpenPos;
  }

  /** Vendor server-side mode: build dropdowns from the server facets
   *  (whole-history distinct values) instead of the current page's rows. */
  private applyServerFacets(): void {
    const toOptions = (arr?: string[]) =>
      (arr || []).map((v) => ({ value: v, label: v }));
    this.lob = toOptions(this.serverFacets['lob']);
    this.leader = toOptions(this.serverFacets['leader']);
    this.location = toOptions(this.serverFacets['location']);
    this.jobTitle = toOptions(this.serverFacets['jobTitle']);
    this.requestStatus = [
      { value: 'Open', label: 'Open', selected: false },
      { value: 'Onhold', label: 'Onhold', selected: false },
      { value: 'Closed', label: 'Closed', selected: false },
    ];
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private cleanupOldFormatFilters(): void {
    try {
      const savedFilters = this.filterPersistenceService.loadFilters(
        this.fieldsVisibility,
      );
      if (savedFilters) {
        const hasOldFormat = Object.values(savedFilters).some(
          (value) =>
            Array.isArray(value) &&
            value.some(
              (item) => typeof item === 'object' && 'selected' in item,
            ),
        );

        if (hasOldFormat) {
          this.filterPersistenceService.clearFilters(this.fieldsVisibility);
          this.hasSavedFilters = false;
        }
      }
    } catch (error) {
    }
  }

  ngOnDestroy() {
    if (this.isDataLoaded && this.filterForm && this.filterForm.dirty) {
      this.saveFilters();
    }
  }

  reset() {
    this.filterPersistenceService.clearFilters(this.fieldsVisibility);
    this.hasSavedFilters = false;
    
    this.showForm = false;
    this.openForm = false;
    this.cdr.detectChanges();

      this.jobTitle = [];
      this.lob = [];
      this.leader = [];
      this.location = [];
      this.sharedByVendor = [];
      this.filterForm = new FormGroup({});
      this.cdr.detectChanges();

        if (this.originalData && this.originalData.length > 0) {
          this.jobTitle = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.JOB_TITLE);
          this.lob = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.LOB_NAME);
          this.sharedByVendor = createVendorOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.VENDOR_NAME);
          this.leader = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.DNT_LEADER_NAME);
          this.location = createLocationOptions(this.originalData);
          const otherStatuses = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.STATUS)
            .filter(status => 
              String(status.value).toLowerCase() !== 'open'
            );
          
          this.requestStatus = [
            { value: 'Open', label: 'Open' },
            ...otherStatuses
          ];
          
          this.rowData = [...this.originalData];
        }
        this.initialzeForm();
        this.formKey++;
        this.cdr.detectChanges();

        setTimeout(() => {
          this.openForm = true;
          this.showForm = true;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.applyDefaultOpenFilter();
          }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.MEDIUM);
        }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.SHORT);
      }

  private applyDefaultOpenFilter(): void {
    const openStatus = { value: 'Open', label: 'Open' };
    
      if (!this.filterForm) {
        setTimeout(() => {
          this.applyDefaultOpenFilter();
      }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.MEDIUM);
          return;
        }
        
        this.filterForm.patchValue({
          selectedStatus: openStatus
        }, { emitEvent: true });
        
        this.filterForm.get('selectedStatus')?.markAsDirty();
        this.filterForm.get('selectedStatus')?.updateValueAndValidity();
        
        if (this.statusDropdown) {
          this.statusDropdown.writeValue(openStatus);
        }
        
        this.cdr.detectChanges();
        
        const filters = {
          jobTitle: null,
          lobName: null,
          location: null,
          status: 'Open',
          desiredStartDate: null,
          createdDate: null,
          dntLeaderName: null,
          vendorName: null,
        };
        
        if (this.fieldsVisibility === PersonaType.OpsTeamOpenPos) {
          this.displayData = this.filterOpenPositions(filters, []);
        } else if (this.fieldsVisibility === PersonaType.HireMgrOpenPos) {
          this.displayData = this.filterOpenPositions(filters, [
            OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.DNT_LEADER_NAME,
            OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.CREATED_DATE,
            OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.LOCATION,
          ]);
        } else {
          this.displayData = this.filterOpenPositions(filters, []);
        }
        this.myOutput.emit(this.displayData);
        this.saveFilters();
        this.checkForSavedFilters();
  }

  saveFilters(): void {
    const currentFilters = this.getCurrentFilterState();
    this.filterPersistenceService.saveFilters(
      this.fieldsVisibility,
      currentFilters,
    );
    this.hasSavedFilters = true;
    this.cdr.detectChanges();
  }

  checkForSavedFilters(): void {
    const previousValue = this.hasSavedFilters;
    this.hasSavedFilters = this.filterPersistenceService.hasFilters(
      this.fieldsVisibility,
    );

    if (previousValue !== this.hasSavedFilters) {
      this.cdr.detectChanges();
    }
  }

  loadSavedFilters(): void {
    const savedFilters = this.filterPersistenceService.loadFilters(
      this.fieldsVisibility,
    );
    if (savedFilters) {
      this.applySavedFilters(savedFilters);
    } else {
      setTimeout(() => {
        this.setDefaultOpenStatus();
        setTimeout(() => {
          this.apply();
        }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.MEDIUM * 2);
      }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.MEDIUM);
    }
  }

  private getCurrentFilterState(): Partial<SavedFilterState> {
    const extractValue = (formValue: any): any => {
      if (!formValue) return null;

      if (typeof formValue === 'object' && formValue.value) {
        return formValue.value;
      }

      if (Array.isArray(formValue)) {
        const selectedItems = formValue.filter(
          (item) => item.selected === true,
        );
        if (selectedItems.length > 1) {
          return selectedItems?.map((item) => item.value);
        } else if (selectedItems.length === 1) {
          return selectedItems[0].value;
        }
        return null;
      }

      return formValue;
    };

    return {
      selectedJobtitle: extractValue(
        this.filterForm.get('selectedJobtitle')?.value,
      ),
      selectedLob: extractValue(this.filterForm.get('selectedLob')?.value),
      selectedStartdate: this.filterForm.get('selectedStartdate')?.value
        ? this.filterForm.get('selectedStartdate')?.value.toISOString?.() ||
        this.filterForm.get('selectedStartdate')?.value
        : null,
      selectedRaisedBy: extractValue(
        this.filterForm.get('selectedRaisedBy')?.value,
      ),
      selectedLeader: extractValue(
        this.filterForm.get('selectedLeader')?.value,
      ),
      selectedRaisedOn: this.filterForm.get('selectedRaisedOn')?.value
        ? this.filterForm.get('selectedRaisedOn')?.value.toISOString?.() ||
        this.filterForm.get('selectedRaisedOn')?.value
        : null,
      selectedLocation: extractValue(
        this.filterForm.get('selectedLocation')?.value,
      ),
      selectedStatus: extractValue(
        this.filterForm.get('selectedStatus')?.value,
      ),
      selectedSharedByVendor: extractValue(
        this.filterForm.get('selectedSharedByVendor')?.value,
      ),
    };
  }

  private applySavedFilters(savedFilters: SavedFilterState): void {
    if (!this.jobTitle || this.jobTitle.length === 0) {
      setTimeout(() => this.applySavedFilters(savedFilters), OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.LONG);
      return;
    }

    const findMatchingOption = (options: any[], value: string): any | null => {
      return options?.find((option) => option.value === value) || null;
    }

    const extractSavedValue = (savedValue: any): string | string[] | null => {
      if (!savedValue) return null;

      if (typeof savedValue === 'string') return savedValue;

      if (Array.isArray(savedValue)) {
        const selectedItems = savedValue.filter(
          (item) => typeof item === 'object' && item.selected === true,
        );

        if (selectedItems.length > 0) {
          return selectedItems.length > 1
            ? selectedItems?.map((item) => item.value)
            : selectedItems[0].value;
        }
        if (savedValue.length > 0 && typeof savedValue[0] === 'string') {
          return savedValue.length > 1 ? savedValue : savedValue[0];
        }
      }

      if (typeof savedValue === 'object' && savedValue.value) {
        return savedValue.value;
      }

      return null;
    };

    const patchValue: any = {};

    const jobTitleValue = extractSavedValue(savedFilters.selectedJobtitle);
    if (jobTitleValue) {
      const matchingJobTitle = this.jobTitle.find(
        (option) => option.value === jobTitleValue,
      );
      if (matchingJobTitle) {
        patchValue.selectedJobtitle = matchingJobTitle;
      }
    }

    const lobValue = extractSavedValue(savedFilters.selectedLob);
    if (lobValue) {
      const matchingLob = this.lob.find((option) => option.value === lobValue);
      if (matchingLob) {
        patchValue.selectedLob = matchingLob;
      }
    }

    const leaderValue = extractSavedValue(savedFilters.selectedLeader);
    if (leaderValue) {
      const matchingLeader = this.leader.find(
        (option) => option.value === leaderValue,
      );
      if (matchingLeader) {
        patchValue.selectedLeader = matchingLeader;
      }
    }

    const locationValue = extractSavedValue(savedFilters.selectedLocation);
    if (locationValue) {
      const matchingLocation = this.location.find(
        (option) => option.value === locationValue,
      );
      if (matchingLocation) {
        patchValue.selectedLocation = matchingLocation;
      }
    }

    const statusValue = extractSavedValue(savedFilters.selectedStatus);
    if (statusValue) {
      if (Array.isArray(statusValue)) {
              const matchingStatuses = statusValue?.map(val => 
                findMatchingOption(this.requestStatus, val)
              ).filter(status => status !== null);
              
              if (matchingStatuses.length > 0) {
                patchValue.selectedStatus = matchingStatuses;
              }
            } else {
              const matchingStatus = findMatchingOption(this.requestStatus, statusValue);
              if (matchingStatus) {
                patchValue.selectedStatus = matchingStatus;
              }
            }
    }

    const vendorValue = extractSavedValue(savedFilters.selectedSharedByVendor);
    if (vendorValue) {
      const matchingVendor = this.sharedByVendor.find(
        (option) => option.value === vendorValue,
      );
      if (matchingVendor) {
        patchValue.selectedSharedByVendor = matchingVendor;
      }
    }

    if (savedFilters.selectedStartdate) {
      const startDate = new Date(savedFilters.selectedStartdate);
      if (!isNaN(startDate.getTime())) {
        patchValue.selectedStartdate = startDate;
      }
    }

    if (savedFilters.selectedRaisedOn) {
      const raisedDate = new Date(savedFilters.selectedRaisedOn);
      if (!isNaN(raisedDate.getTime())) {
        patchValue.selectedRaisedOn = raisedDate;
      }
    }

    if (savedFilters.selectedRaisedBy) {
      patchValue.selectedRaisedBy = savedFilters.selectedRaisedBy;
    }

    const hasFiltersToApply = Object.values(patchValue).some(
      (value) => value !== null && value !== undefined,
    );

    setTimeout(() => {
      this.filterForm.patchValue(patchValue);
      this.filterForm.markAsDirty();
      this.filterForm.updateValueAndValidity();
      this.cdr.detectChanges();
      if (this.statusDropdown && patchValue.selectedStatus) {
        this.statusDropdown.writeValue(patchValue.selectedStatus);
      }
      if (this.jobTitleDropdown && patchValue.selectedJobtitle) {
        this.jobTitleDropdown.writeValue(patchValue.selectedJobtitle);
      }
      if (this.lobDropdown && patchValue.selectedLob) {
        this.lobDropdown.writeValue(patchValue.selectedLob);
      }
      if (this.leaderDropdown && patchValue.selectedLeader) {
        this.leaderDropdown.writeValue(patchValue.selectedLeader);
      }
      
      this.openForm = false;
      this.cdr.detectChanges();
      
      setTimeout(() => {
        this.openForm = true;
        this.cdr.detectChanges();
        
        setTimeout(() => {
          if (hasFiltersToApply) {
            this.apply();
          } else {
            this.setDefaultOpenStatus();
            setTimeout(() => {
              this.apply();
            }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.MEDIUM);
          }
        }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.MEDIUM);
      }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.SHORT);
    }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.SHORT);
  }

  preventInvalidKeyPress(event: KeyboardEvent) {
    restrictInvalidKeyPress(event);
  }

  trackByFormKey(): number {
    return this.formKey;
  }

  getDropdownLabel(controlName: string): string {
    const value = this.filterForm.get(controlName)?.value;
    if (!value || value === null) return '';
    return value?.label || '';
  }

  getDropdownValue(controlName: string): string {
    const value = this.filterForm.get(controlName)?.value;
    if (!value || value === null) return '';
    return value?.value || '';
  }

  initialzeForm() {
    const defaultOpenStatus = { value: 'Open', label: 'Open' };
    if (!this.requestStatus || this.requestStatus.length === 0) {
      this.requestStatus = [defaultOpenStatus];
    } else if (!this.requestStatus.some(status => status.value === 'Open')) {
      this.requestStatus = [defaultOpenStatus, ...this.requestStatus];
    }
    this.filterForm = this.fb.group({
      selectedJobtitle: [null],
      selectedLob: [null],
      selectedStartdate: [null],
      selectedRaisedBy: [null],
      selectedLeader: [null],
      selectedRaisedOn: [null],
      selectedLocation: [null],
      selectedStatus: [defaultOpenStatus],
      selectedSharedByVendor: [null],
    });

    if (this.isDataLoaded && !this.hasSavedFilters) {
      setTimeout(() => {
        if (this.filterForm) {
          this.statusDropdown?.writeValue?.(defaultOpenStatus);
          this.filterForm.patchValue({ selectedStatus: defaultOpenStatus }, { emitEvent: true });
          this.filterForm.get('selectedStatus')?.markAsDirty();
          this.filterForm.updateValueAndValidity();
          this.cdr.detectChanges();
          setTimeout(() => {
            this.apply();
          }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.SHORT);
        }
      }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.SHORT);
    }
  }
  
  private getDefaultOpenStatusOption(): dropdownValues | null {
    if (!this.requestStatus || this.requestStatus.length === 0) {
      return null;
    } 
    const defaultOpenStatus: dropdownValues = {
      value: 'Open',
      label: 'Open'
    };
    
    let openStatus = this.requestStatus.find(
      (status) => status.value === 'Open'
    );
    
    if (!openStatus) {
      openStatus = this.requestStatus.find(
        (status) => typeof status.value === 'string' && 
           status.value.toLowerCase() === 'open'
      );
    }
    
    if (!openStatus) {
      openStatus = this.requestStatus.find(
        (status) => typeof status.label === 'string' && 
                    status.label.toLowerCase() === 'open'
      );
    }
    return openStatus || defaultOpenStatus;
  }

  private setDefaultOpenStatus(): void {
    const openStatus = this.getDefaultOpenStatusOption();
    
    if (openStatus && this.filterForm) {
      if (!this.requestStatus.some(status => status.value === openStatus.value)) {
        this.requestStatus = [openStatus, ...this.requestStatus];
      }
      
      this.filterForm.patchValue({
        selectedStatus: openStatus
      }, { emitEvent: true });
      
      this.filterForm.get('selectedStatus')?.markAsDirty();
      this.filterForm.get('selectedStatus')?.updateValueAndValidity();
      
      if (this.statusDropdown) {
        setTimeout(() => {
          this.statusDropdown.writeValue(openStatus);
        }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.SHORT);
      }
      
      this.cdr.detectChanges();
      setTimeout(() => {
        this.apply();
      }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.MEDIUM);
    }
  }

  /* status checkboxes — the control's value may be a plain string array
     (user toggles), a single {label,value} object (default Open patch), or
     null; normalize on read and always write back a string array */
  statusOpen = false;

  get statusSummary(): string {
    const v = this.filterForm?.get('selectedStatus')?.value;
    let arr: any[] = [];
    if (Array.isArray(v)) arr = v.map((x: any) => (typeof x === 'object' ? x?.value : x)).filter(Boolean);
    else if (v && typeof v === 'object' && v.value) arr = [v.value];
    else if (v) arr = [v];
    if (!arr.length) return 'All';
    if (arr.length <= 2) return arr.join(', ');
    return `${arr.slice(0, 2).join(', ')} +${arr.length - 2} more`;
  }

  isStatusChecked(status: string | number): boolean {
    const v = this.filterForm?.get('selectedStatus')?.value;
    if (!v) return false;
    if (Array.isArray(v)) return v.some((x: any) => (typeof x === 'string' ? x : x?.value) === status);
    if (typeof v === 'object') return v.value === status;
    return v === status;
  }

  toggleStatus(status: string | number): void {
    const ctrl = this.filterForm?.get('selectedStatus');
    if (!ctrl) return;
    const v = ctrl.value;
    let arr: (string | number)[] = [];
    if (Array.isArray(v)) arr = v.map((x: any) => (typeof x === 'string' ? x : x?.value)).filter(Boolean);
    else if (v && typeof v === 'object' && v.value) arr = [v.value];
    else if (typeof v === 'string' && v) arr = [v];
    arr = arr.includes(status) ? arr.filter((x) => x !== status) : [...arr, status];
    ctrl.setValue(arr.length ? arr : null);
    ctrl.markAsDirty();
  }

  apply(): void {
    const filters = this.getFiltersFromForm();

    // Vendor server-side mode: emit the selected criteria for a server
    // refetch instead of filtering the current page in memory. Status is
    // multi-select, sent as a comma-separated list the server matches with IN.
    if (this.isVendorServerMode()) {
      const statusCsv = Array.isArray(filters.status)
        ? filters.status.filter((s: any) => !!s).join(',')
        : filters.status;
      this.filterCriteria.emit({
        status: statusCsv || 'Open',
        lob: filters.lobName || undefined,
        leader: filters.dntLeaderName || undefined,
        location: filters.location?.value || undefined,
      });
      this.saveFilters();
      this.checkForSavedFilters();
      return;
    }

    if (this.fieldsVisibility === PersonaType.OpsTeamOpenPos) {
      this.displayData = this.filterOpenPositions(filters, []);
    } else if (this.fieldsVisibility === PersonaType.HireMgrOpenPos) {
      this.displayData = this.filterOpenPositions(filters, [
        OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.DNT_LEADER_NAME,
        OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.CREATED_DATE,
        OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.LOCATION,
      ]);
    } else {
      this.displayData = this.filterOpenPositions(filters, []);
    }

    this.myOutput.emit(this.displayData);
    this.saveFilters();
    this.checkForSavedFilters();
  }

  private getFiltersFromForm() {
    const getStringFilterValue = (field: string) => {
      const value = this.filterForm.get(field)?.value || null;
      if (value && typeof value === 'object' && value.value) {
        return value.value;
      }
      return value;
    };

    const getObjectFilterValue = (field: string) => {
      const value = this.filterForm.get(field)?.value || null;
      if (value && typeof value === 'object' && value.value) {
        return { value: value.value };
      }
      return value ? { value: value } : null;
    };

     const getStatusFilterValue = (field: string) => {
    const value = this.filterForm.get(field)?.value || null;
    if (!value) return null;

    if (Array.isArray(value)) {
      const selectedValues = value
        .filter(item => typeof item === 'string' || item?.selected === true)
        .map(item => (typeof item === 'string' ? item : item.value || item));
      return selectedValues.length > 0 ? selectedValues : null;
    }

    if (typeof value === 'object' && value.value) {
      return value.value;
    }
    
    return value;
  }

    const formattedStartDate = getStringFilterValue('selectedStartdate')
      ? formatSingleDate(
        getStringFilterValue('selectedStartdate'),
        yearDateFormat.date,
      )
      : null;

    const formattedCreatedDate = getStringFilterValue('selectedRaisedOn')
      ? formatSingleDate(
        getStringFilterValue('selectedRaisedOn'),
        yearDateFormat.date,
      )
      : null;

    return {
      jobTitle: getStringFilterValue('selectedJobtitle'),
      lobName: getStringFilterValue('selectedLob'),
      location: getObjectFilterValue('selectedLocation'),
      status: getStatusFilterValue('selectedStatus'),
      desiredStartDate: formattedStartDate,
      createdDate: formattedCreatedDate,
      dntLeaderName: getStringFilterValue('selectedLeader'),
      vendorName: getObjectFilterValue('selectedSharedByVendor'),
    };
  }

  private filterOpenPositions(filters: any, excludeKeys: string[]) {
    const filterCriteria = { ...filters };
    excludeKeys.forEach((key) => delete filterCriteria[key]);

    if (this.fieldsVisibility === PersonaType.HireMgrOpenPos) {
      return this.customFilterForHiringManager(filterCriteria);
    }

    return this.customFilterForHiringManager(filterCriteria);
  }

  private customFilterForHiringManager(filters: any) {
    const result = this.originalData.filter((item: any) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key];
        if (!filterValue) return true;
        if (key === 'status') {
        if (Array.isArray(filterValue)) {
          const statusValues = filterValue?.map((status: any) => 
            typeof status === 'string' ? status.toLowerCase() : status?.value?.toLowerCase()
          ).filter(Boolean);
          
          return typeof item[key] === 'string' && 
                 statusValues.includes(item[key].toLowerCase());
        } else {
          const statusValue = typeof filterValue === 'string' ? 
                             filterValue.toLowerCase() : 
                             filterValue?.value?.toLowerCase();
          
          return typeof item[key] === 'string' && 
                 item[key].toLowerCase() === statusValue;
        }
      }

        if (key === 'desiredStartDate') {
          return item[key] === filterValue;
        }

        if (key === 'vendorName' && filterValue?.value) {
          if (Array.isArray(item[key])) {
            return item[key]?.some(
              (vendor: string) =>
              typeof vendor === 'string' &&
              (
                vendor?.trim().toLowerCase().includes(filterValue?.value?.trim().toLowerCase())
              )
            );
          } else {
            return (
              typeof item[key] === 'string' &&
              item[key]
                ?.toLowerCase()
                ?.includes(filterValue.value.toLowerCase())
            );
          }
        }

        if (typeof filterValue === 'string') {
          return (
            typeof item[key] === 'string' &&
            item[key]?.toLowerCase()?.includes(filterValue.toLowerCase())
          );
        } else if (filterValue?.value !== undefined) {
          return (
            typeof item[key] === 'string' &&
            item[key]?.toLowerCase()?.includes(filterValue.value.toLowerCase())
          );
        }

        return true;
      });
    });

    return result;
  }

  private loadFormData() {
    // Vendor server-side mode gets its dropdowns from serverFacets (via
    // ngOnChanges), not from a data-signal fetch — skip the client load.
    if (this.isVendorServerMode()) {
      this.initialzeForm();
      this.applyServerFacets();
      return;
    }
    this.fieldsVisibility === PersonaType.HireMgrOpenPos
      ? this.openPositionsService.getOpenPositions()
      : this.opsOpenPositionsService.getOpenPositions();

    this.requestStatus = [{ value: 'Open', label: 'Open' }];
    this.initialzeForm();

    interval(OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.LONG * 2)
      .pipe(takeWhile(() => !this.isDataLoaded))
      .subscribe({
        next: () => {
          let signalData;
          signalData =
            this.fieldsVisibility === PersonaType.vendorRepOpenPos
              ? this.vendorOpenpositionsService.openPositionsSignal()
              : this.fieldsVisibility === PersonaType.HireMgrOpenPos
                ? this.openPositionsService.openPositionsSignal()
                : this.opsOpenPositionsService.openPositionsSignal();

          if (signalData && signalData?.data) {
            this.originalData = signalData?.data?.map(mapHiringRequest);
            this.rowData = [...this.originalData];
            this.displayData = [...this.originalData];
            this.jobTitle = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.JOB_TITLE);
            this.lob = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.LOB_NAME);
            this.sharedByVendor = createVendorOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.VENDOR_NAME);
            this.leader = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.DNT_LEADER_NAME);
            this.location = createLocationOptions(this.originalData);
            
            const otherStatuses = createOptions(this.originalData, OPEN_POSITION_FILTER_CONSTANTS.FIELD_NAMES.STATUS)
              .filter(status => 
                String(status.value).toLowerCase() !== 'open'
              );
            
            this.requestStatus = [
              { value: 'Open', label: 'Open' },
              ...otherStatuses
            ];
            
            this.isDataLoaded = true;
            this.cdr.detectChanges();
            
            setTimeout(() => {
              this.loadSavedFilters();
            }, OPEN_POSITION_FILTER_CONSTANTS.TIMEOUT_VALUES.LONG);
          }
        },
        error: (error) => {
          this.isDataLoaded = true;
          this.globalError.logComponentError(error);
        },
      });
  }

  // Helper methods for template logic
  isVendorOrOpsVisible(): boolean {
    return this.fieldsVisibility === this.personaType.vendorRepOpenPos ||
           this.fieldsVisibility === this.personaType.OpsTeamOpenPos;
  }

  isHireMgrOrOpsVisible(): boolean {
    return this.fieldsVisibility === this.personaType.HireMgrOpenPos ||
           this.fieldsVisibility === this.personaType.OpsTeamOpenPos;
  }
}
